import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';

const EMPTY_FORM = {
    name: '',
    managerId: '',
    memberIds: [],
    scoringMode: 'average'
};

const SCORING_MODE_OPTIONS = [
    { label: 'Promedio', value: 'average' },
    { label: 'Suma', value: 'sum' }
];

const getId = (value) => value?._id || value || '';

const buildInitialForm = (team) => {
    if (!team) return EMPTY_FORM;

    return {
        name: team.name || '',
        managerId: getId(team.managerId),
        memberIds: Array.isArray(team.memberIds) ? team.memberIds.map(getId).filter(Boolean) : [],
        scoringMode: team.scoringMode === 'sum' ? 'sum' : 'average'
    };
};

const OkrTeamDialog = ({ visible, team, users, saving, onHide, onSubmit }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const isEditMode = Boolean(team?._id);

    useEffect(() => {
        if (visible) {
            setForm(buildInitialForm(team));
            setErrors({});
        }
    }, [team, visible]);

    const title = useMemo(() => (isEditMode ? 'Editar Equipo' : 'Agregar Equipo'), [isEditMode]);

    const userOptions = useMemo(
        () =>
            (users || []).map((user) => ({
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Usuario sin nombre',
                value: user._id
            })),
        [users]
    );

    const updateField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setErrors((current) => ({ ...current, [field]: null }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.name?.trim()) nextErrors.name = 'El nombre del equipo es obligatorio.';
        if (!form.managerId) nextErrors.managerId = 'Selecciona un Manager OKR.';

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;

        onSubmit({
            name: form.name.trim(),
            managerId: form.managerId,
            memberIds: form.memberIds || [],
            scoringMode: form.scoringMode || 'average'
        });
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={onHide} disabled={saving} />
            <Button label={isEditMode ? 'Guardar cambios' : 'Agregar equipo'} icon="pi pi-check" onClick={handleSubmit} loading={saving} />
        </div>
    );

    return (
        <Dialog header={title} visible={visible} onHide={onHide} footer={footer} className="w-full md:w-30rem" modal draggable={false}>
            <div className="formgrid grid">
                <div className="field col-12">
                    <label htmlFor="okr-team-name" className="font-medium">Nombre del equipo</label>
                    <InputText
                        id="okr-team-name"
                        value={form.name}
                        onChange={(event) => updateField('name', event.target.value)}
                        className={`w-full mt-2 ${errors.name ? 'p-invalid' : ''}`}
                    />
                    {errors.name ? <small className="p-error">{errors.name}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-team-manager" className="font-medium">Manager OKR</label>
                    <Dropdown
                        id="okr-team-manager"
                        value={form.managerId}
                        options={userOptions}
                        onChange={(event) => updateField('managerId', event.value)}
                        filter
                        showClear
                        className={`w-full mt-2 ${errors.managerId ? 'p-invalid' : ''}`}
                        placeholder="Selecciona un Manager OKR"
                    />
                    <small className="block text-color-secondary mt-1">Puede administrar la configuración OKR del equipo.</small>
                    {errors.managerId ? <small className="p-error">{errors.managerId}</small> : null}
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-team-members" className="font-medium">Colaboradores</label>
                    <MultiSelect
                        id="okr-team-members"
                        value={form.memberIds}
                        options={userOptions}
                        onChange={(event) => updateField('memberIds', event.value)}
                        filter
                        display="chip"
                        className="w-full mt-2"
                        placeholder="Selecciona colaboradores"
                    />
                    <small className="block text-color-secondary mt-1">Pueden ver sus OKRs asignados y actualizar su avance semanal.</small>
                </div>

                <div className="field col-12">
                    <label htmlFor="okr-team-scoring-mode" className="font-medium">Modo de puntaje</label>
                    <Dropdown
                        id="okr-team-scoring-mode"
                        value={form.scoringMode}
                        options={SCORING_MODE_OPTIONS}
                        onChange={(event) => updateField('scoringMode', event.value)}
                        className="w-full mt-2"
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default OkrTeamDialog;
